// @ds/editable/RadioGroupField — Radio.Group widget used by the field-type
// registry dispatcher.
//
// Split out of `fieldTypeRegistry.tsx` so that module holds no component.

import React, { useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { Radio } from "antd";

// ─── Radio-group widget ─────────────────────────────────────────────────────
//
// AntD spreads `data-testid` onto the hidden opacity-0 `<input>` inside each
// Radio.Button — not the clickable `.ant-radio-button-wrapper` label — so a
// `getByTestId(...).click()` can't reliably actuate it. Stamp the option testid
// directly on the wrapper (in render order) via a ref, so e2e specs click the
// visible control. No data-testid on the input → no double-match.
interface RadioGroupFieldProps {
  testId?: string;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
  options: Array<{ value: string | number; label: ReactNode }>;
  bind: Record<string, unknown>;
}

// `injected` captures the props Form.Item adds via cloneElement (value/onChange/
// id) in the form context — they must reach the real <Radio.Group>, or the
// selection never commits to the form. In the inline-edit context those come
// through `bind` (from ctx) instead; forwarding both covers every caller.
export const RadioGroupField: React.FC<RadioGroupFieldProps & Record<string, unknown>> = ({
  testId, disabled, size, options, bind, ...injected
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!testId || !ref.current) return;
    const wrappers = ref.current.querySelectorAll(".ant-radio-button-wrapper");
    options.forEach((opt, i) => {
      wrappers[i]?.setAttribute("data-testid", `${testId}-option-${opt.value}`);
    });
  });
  return (
    <span ref={ref} className="ds-display-contents">
      <Radio.Group
        {...bind}
        {...injected}
        disabled={disabled}
        {...(size ? { size } : {})}
        {...(testId ? { "data-testid": testId } : {})}
      >
        {options.map((opt) => (
          <Radio.Button key={String(opt.value)} value={opt.value}>
            {opt.label}
          </Radio.Button>
        ))}
      </Radio.Group>
    </span>
  );
};

